import React, { useState, useEffect, useCallback, useRef } from 'react';
import api from '../utils/api';
import { formatCurrency, formatDate } from '../utils/formatters';

// Surfaces TikTok products that look like the same logical listing but
// were split into two products rows (sync used to insert a fresh stub
// whenever TikTok rolled a sku_id on rename/reprice). Admin reviews each
// auto-detected pair, OR merges any two products manually if auto-detection
// missed them (which it will whenever one of the rows has never been sold,
// since there are no line items to derive the parent listing id from).
export default function DuplicateReviewBanner({ onMergeApplied }) {
  const [candidates, setCandidates] = useState([]);
  const [stats, setStats] = useState(null);
  const [expanded, setExpanded] = useState(false);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState(null);
  const [manualOpen, setManualOpen] = useState(false);
  const [swapped, setSwapped] = useState(new Set()); // group_ids the admin has flipped

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await api.get('/api/admin/products/duplicate-candidates');
      setCandidates(data.candidates || []);
      setStats(data.stats || null);
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.error || err.message;
      setError(status ? `HTTP ${status}: ${detail}` : detail);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  function effectiveSides(c) {
    return swapped.has(c.group_id) ? { keep: c.drop, drop: c.keep } : { keep: c.keep, drop: c.drop };
  }

  function toggleSwap(groupId) {
    setSwapped(prev => {
      const next = new Set(prev);
      if (next.has(groupId)) next.delete(groupId); else next.add(groupId);
      return next;
    });
  }

  async function handleMerge(c) {
    const { keep, drop } = effectiveSides(c);
    setBusy(c.group_id); setError(null);
    try {
      await api.post('/api/admin/products/merge', { keep_id: keep.id, drop_id: drop.id });
      await refresh();
      if (onMergeApplied) onMergeApplied();
    } catch (err) {
      setError(err?.response?.data?.error || err.message);
    } finally { setBusy(null); }
  }

  async function handleDismiss(c) {
    const { drop } = effectiveSides(c);
    setBusy(c.group_id); setError(null);
    try {
      await api.post('/api/admin/products/merge-dismiss', { drop_id: drop.id });
      await refresh();
    } catch (err) {
      setError(err?.response?.data?.error || err.message);
    } finally { setBusy(null); }
  }

  const hasCandidates = candidates.length > 0;
  const palette = error
    ? { bg: '#fef2f2', border: '#dc2626', text: '#991b1b' }
    : hasCandidates
      ? { bg: '#eff6ff', border: '#3b82f6', text: '#1e3a8a' }
      : { bg: '#f0fdf4', border: '#22c55e', text: '#166534' };

  return (
    <div style={{
      marginBottom: '16px',
      padding: '12px 16px',
      borderRadius: '8px',
      background: palette.bg,
      border: `1px solid ${palette.border}`,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ fontSize: '13px', color: palette.text }}>
          {loading && <>Looking for duplicate products…</>}
          {!loading && error && <><strong>Duplicate review failed.</strong> {error}</>}
          {!loading && !error && hasCandidates && (
            <>
              <strong>{candidates.length}</strong> auto-detected duplicate pair{candidates.length === 1 ? '' : 's'}.
              {' '}
              <span style={{ color: '#1e40af' }}>
                Merging keeps the older row's COGS with the newer row's name and price.
              </span>
            </>
          )}
          {!loading && !error && !hasCandidates && (
            <>
              <strong>No duplicate products auto-detected.</strong>{' '}
              {stats && (
                <span style={{ color: '#15803d' }}>
                  Scanned {stats.total_products} products ({stats.backfilled_with_parent} have a derived parent listing) → {stats.groups_inspected} (parent + variant) groups → 0 pairs.
                </span>
              )}
              {' '}Use Manual merge for pairs the auto-check can't see (e.g. a row that's never been sold has no line items to identify its parent listing).
            </>
          )}
        </div>
        <div style={{ display: 'flex', gap: '6px' }}>
          <button className="btn btn-secondary btn-sm" onClick={refresh}>Recheck</button>
          {hasCandidates && (
            <button className="btn btn-secondary btn-sm" onClick={() => setExpanded(v => !v)}>{expanded ? 'Hide' : 'Review'}</button>
          )}
          <button
            className={manualOpen ? 'btn btn-secondary btn-sm' : 'btn btn-primary btn-sm'}
            onClick={() => setManualOpen(v => !v)}
          >
            {manualOpen ? 'Close manual merge' : 'Manual merge'}
          </button>
        </div>
      </div>

      {expanded && hasCandidates && (
        <div style={{ marginTop: '12px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {candidates.map(c => {
            const sides = effectiveSides(c);
            const previewCogs = sides.keep.cogs != null ? sides.keep.cogs : sides.drop.cogs;
            return (
              <div key={c.group_id} style={{ padding: '12px', borderRadius: '6px', background: '#fff', border: '1px solid #cbd5e1' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
                  <div style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>
                    Matched on: <strong>{c.match_reason}</strong>
                    {c.parent_id && <> · Parent listing <code>{c.parent_id}</code></>}
                    {c.variant_name && <> · Variant <strong>{c.variant_name}</strong></>}
                  </div>
                  <button
                    className="btn btn-secondary btn-sm"
                    onClick={() => toggleSwap(c.group_id)}
                    title="Swap which row keeps its identity"
                  >
                    ↔ Swap
                  </button>
                </div>
                <div className="auto-stack" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                  <RowPanel label="KEEP (this row's name, price, sku_id survive)" row={sides.keep} highlight="#dcfce7" />
                  <RowPanel label="DROP (folds in — its COGS fills KEEP if needed)" row={sides.drop} highlight="#fee2e2" />
                </div>
                <div style={{ marginTop: '8px', padding: '8px', borderRadius: '4px', background: '#f1f5f9', fontSize: '12px', color: 'var(--text-secondary)' }}>
                  <strong style={{ color: 'var(--text-primary)' }}>After merge:</strong>{' '}
                  {sides.keep.product_name} · {formatCurrency(sides.keep.price)} · COGS {previewCogs != null ? formatCurrency(previewCogs) : '—'}
                </div>
                <div style={{ marginTop: '8px', display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                  <button className="btn btn-secondary btn-sm" disabled={busy === c.group_id} onClick={() => handleDismiss(c)}>Not duplicates</button>
                  <button className="btn btn-primary btn-sm" disabled={busy === c.group_id} onClick={() => handleMerge(c)}>
                    {busy === c.group_id ? 'Merging…' : 'Merge'}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {manualOpen && (
        <ManualMergeForm
          onMerged={() => { refresh(); if (onMergeApplied) onMergeApplied(); }}
        />
      )}
    </div>
  );
}

function RowPanel({ label, row, highlight }) {
  return (
    <div style={{ padding: '8px', borderRadius: '4px', background: highlight }}>
      <div style={{ fontSize: '10px', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '4px' }}>{label}</div>
      <div style={{ fontSize: '13px', fontWeight: 500 }}>{row.product_name}</div>
      <div style={{ fontSize: '12px', color: 'var(--text-secondary)', marginTop: '4px' }}>
        {formatCurrency(row.price)}
        {row.cogs != null && <> · COGS {formatCurrency(row.cogs)}</>}
        {row.last_sold_at && <> · last sold {formatDate(row.last_sold_at)}</>}
      </div>
      <div style={{ fontSize: '10px', color: 'var(--text-secondary)', marginTop: '2px' }}>
        sku_id <code>{row.product_id}</code>
      </div>
    </div>
  );
}

// Search-based manual merge. Two slots: KEEP (left) and DROP (right). Each
// slot has a search input; results from /api/admin/products?search= populate
// a small picker; click a result to fill the slot. Once both are filled,
// preview the post-merge result and confirm.
function ManualMergeForm({ onMerged }) {
  const [keep, setKeep] = useState(null);
  const [drop, setDrop] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  async function handleMerge() {
    if (!keep || !drop) return;
    setBusy(true); setError(null);
    try {
      await api.post('/api/admin/products/merge', { keep_id: keep.id, drop_id: drop.id });
      setKeep(null); setDrop(null);
      if (onMerged) onMerged();
    } catch (err) {
      setError(err?.response?.data?.error || err.message);
    } finally { setBusy(false); }
  }

  const previewCogs = (keep && keep.cogs != null) ? keep.cogs : (drop && drop.cogs);

  return (
    <div style={{ marginTop: '12px', padding: '12px', borderRadius: '6px', background: '#fff', border: '1px solid #cbd5e1' }}>
      <div style={{ fontSize: '13px', fontWeight: 600, marginBottom: '8px' }}>Manually merge two products</div>
      <div style={{ fontSize: '12px', color: 'var(--text-secondary)', marginBottom: '12px' }}>
        Search each side, pick the rows to combine. <strong>KEEP</strong>'s name, price, and sku_id all survive. <strong>DROP</strong> is deleted; its sku_id is recorded as an alias so historical orders still resolve. If KEEP has no COGS but DROP does, COGS is pulled forward.
      </div>

      <div className="auto-stack" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
        <ProductPicker label="KEEP (this row's name, price, sku_id survive)" selected={keep} onSelect={setKeep} highlight="#dcfce7" />
        <ProductPicker label="DROP (gets folded in)"                          selected={drop} onSelect={setDrop} highlight="#fee2e2" />
      </div>

      {keep && drop && keep.id === drop.id && (
        <div style={{ marginTop: '8px', color: '#b91c1c', fontSize: '12px' }}>
          Keep and Drop must be different rows.
        </div>
      )}

      {keep && drop && keep.id !== drop.id && (
        <div style={{ marginTop: '8px', padding: '8px', borderRadius: '4px', background: '#f1f5f9', fontSize: '12px', color: 'var(--text-secondary)' }}>
          <strong style={{ color: 'var(--text-primary)' }}>After merge:</strong>{' '}
          {keep.product_name} · {formatCurrency(keep.price)} · COGS {previewCogs != null ? formatCurrency(previewCogs) : '—'}
        </div>
      )}

      {error && (
        <div style={{ marginTop: '8px', color: '#b91c1c', fontSize: '12px' }}>{error}</div>
      )}

      <div style={{ marginTop: '8px', display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
        <button
          className="btn btn-primary btn-sm"
          disabled={busy || !keep || !drop || keep.id === drop.id}
          onClick={handleMerge}
        >
          {busy ? 'Merging…' : 'Merge'}
        </button>
      </div>
    </div>
  );
}

function ProductPicker({ label, selected, onSelect, highlight }) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const debounceRef = useRef(null);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!query || query.length < 2) { setResults([]); return; }
    setSearching(true);
    debounceRef.current = setTimeout(async () => {
      try {
        const { data } = await api.get(`/api/admin/products?search=${encodeURIComponent(query)}&limit=15`);
        setResults(data.products || []);
      } catch (err) {
        setResults([]);
      } finally {
        setSearching(false);
      }
    }, 200);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
  }, [query]);

  return (
    <div style={{ padding: '8px', borderRadius: '4px', background: highlight }}>
      <div style={{ fontSize: '10px', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '6px' }}>{label}</div>
      {selected ? (
        <div>
          <div style={{ fontSize: '13px', fontWeight: 500 }}>{selected.product_name}</div>
          <div style={{ fontSize: '12px', color: 'var(--text-secondary)', marginTop: '2px' }}>
            {formatCurrency(selected.price)}
            {selected.cogs != null && <> · COGS {formatCurrency(selected.cogs)}</>}
          </div>
          <div style={{ fontSize: '10px', color: 'var(--text-secondary)', marginTop: '2px' }}>
            sku_id <code>{selected.product_id}</code>
          </div>
          <button className="btn btn-secondary btn-sm" style={{ marginTop: '6px' }} onClick={() => onSelect(null)}>Change</button>
        </div>
      ) : (
        <>
          <input
            type="text"
            placeholder="Search name or sku_id (min 2 chars)…"
            value={query}
            onChange={e => setQuery(e.target.value)}
            style={{ width: '100%', padding: '6px 8px', fontSize: '12px' }}
          />
          {searching && <div style={{ fontSize: '11px', color: 'var(--text-secondary)', marginTop: '4px' }}>Searching…</div>}
          {results.length > 0 && (
            <div style={{ marginTop: '6px', maxHeight: '220px', overflowY: 'auto', background: '#fff', border: '1px solid #cbd5e1', borderRadius: '4px' }}>
              {results.map(r => (
                <div
                  key={r.id}
                  onClick={() => onSelect(r)}
                  style={{ padding: '6px 8px', cursor: 'pointer', borderBottom: '1px solid #f1f5f9', fontSize: '12px' }}
                  onMouseEnter={e => e.currentTarget.style.background = '#f8fafc'}
                  onMouseLeave={e => e.currentTarget.style.background = '#fff'}
                >
                  <div style={{ fontWeight: 500 }}>{r.product_name}</div>
                  <div style={{ fontSize: '10px', color: 'var(--text-secondary)' }}>
                    sku_id <code>{r.product_id}</code> · {formatCurrency(r.price)}
                    {r.cogs != null && <> · COGS {formatCurrency(r.cogs)}</>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}
